import React, { FC, useContext, useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Spinner from '../../../components/bootstrap/Spinner';
import { Eye, EyeOff } from 'lucide-react';
import { passwordErrors, emailErrors, errorConfirmPassword, errorOTP } from './Validation';
import 'react-toastify/dist/ReactToastify.css';
import Label from '../../../components/bootstrap/forms/Label';
import { useUser } from '../../../contexts/UserContext';
import useDarkMode from '../../../hooks/useDarkMode';
import { centralApi } from '../../AxiosInstance/CreateInstance';
import {
	BsShieldCheck,
	BsClockHistory,
	BsPeople,
} from "react-icons/bs";

interface ILoginHeaderProps {
	isForgot?: boolean;
	isOtpScreen?: boolean;
}

const LoginHeader: FC<ILoginHeaderProps> = ({ isForgot, isOtpScreen }) => {
	const { darkModeStatus } = useDarkMode();
	const headerStyle = {
		color: darkModeStatus ? 'rgb(221 226 230 / 75%)' : '#343a40',
		textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
	};

	if (isForgot && !isOtpScreen) {
		return (
			<>
				<div className='text-center text-light h1 fw-bold mt-5' style={headerStyle}>
					Forgot Password
				</div>
				<div className='text-center text-light opacity-75 mb-5' style={headerStyle}>
					Enter your registerd email to receive an OTP
				</div>
			</>
		);
	}
	if (isOtpScreen) {
		return (
			<>
				<div className='text-center text-light h1 fw-bold mt-2' style={headerStyle}>
					Reset Password
				</div>
				<div className='text-center text-light opacity-75 mb-1' style={headerStyle}>
					Enter OTP and new password to reset your password
				</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center text-white h1 fw-bold mt-5' style={headerStyle}>
				Welcome Back
			</div>
			<div className='text-center text-light opacity-75 mb-5' style={headerStyle}>
				Sign in to continue!
			</div>
		</>
	);
};

LoginHeader.defaultProps = {
	isForgot: false,
	isOtpScreen: false,
};

interface ILoginProps {
	isSignUp?: boolean;
}

const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const [isPending, startTransition] = useTransition();
	const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);
	const [isForgotPassword, setForgotPassword] = useState<boolean>(false);
	const [forgotText, setForgotText] = useState<string>('Forgot Password?');
	const [otp, setOtp] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState(false);
	const [viewPass, setViewPass] = useState(false);
	const [viewConPass, setViewConPass] = useState(false);
	const { user, setUser } = useUser();
	const { darkModeStatus } = useDarkMode();
	const navigate = useNavigate();

	const handleOnClick = () => {
		startTransition(() => {
			navigate('/Dashboard');
		});
	};

	const handleForgotPassword = () => {
		setForgotPassword(!isForgotPassword);
		setForgotText(isForgotPassword ? 'Forgot Password?' : 'Back To Login');
		setOtp(false);
		setIsLoading(false);
		formik.setErrors({});
		formik.setValues({
			...formik.values,
			resetEmail: '',
			resetOTP: '',
			resetNewPassword: '',
			resetNewConfirmPassword: '',
			resetPasssordEmail: '',
			loginUsername: '',
			loginPassword: '',
		});
	};

	const handleOTP = () => {
		if (emailErrors(formik.values.resetEmail) !== '') {
			formik.setFieldError('resetEmail', emailErrors(formik.values.resetEmail));
			setIsLoading(false);
		} else {
			setIsLoading(true);
			centralApi
				.post(`userService/user/forgotPassword`, {
					email: formik.values.resetEmail,
				})
				.then((res) => {
					setTimeout(() => {
						toast.success('OTP send successfully', {
							closeOnClick: true,
						});
						setOtp(true);
						setIsLoading(false);
					}, 1000);
				})
				.catch((err) => {
					toast.error('Cannot send OTP to this email address', {
						closeOnClick: true,
					});
					formik.setFieldError('resetEmail', 'Email does not exist');
					setIsLoading(false);
				});
		}
	};

	const handleBackToLogin = () => {
		setOtp(false);
		setForgotPassword(false);
		setForgotText('Forgot Password?');
		setIsLoading(false);
		formik.setValues({
			...formik.values,
			resetEmail: '',
			resetOTP: '',
			resetNewPassword: '',
			resetNewConfirmPassword: '',
			resetPasssordEmail: '',
			loginUsername: '',
			loginPassword: '',
		});
		formik.setErrors({});
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
			resetEmail: '',
			resetPasssordEmail: '',
			resetNewPassword: '',
			resetNewConfirmPassword: '',
			resetOTP: '',
			userName: '',
			userSurName: '',
			userEmail: '',
			userPassword: '',
			resetUserCurrentPassword: '',
		},
		validate: (values) => {
			const errors: {
				loginUsername?: string;
				loginPassword?: string;
				resetEmail?: string;
				resetNewPassword?: string;
				resetNewConfirmPassword?: string;
				userName?: string;
				userSurName?: string;
				userEmail?: string;
				userPassword?: string;
				resetUserCurrentPassword?: string;
				resetOTP?: string;
				resetPasssordEmail?: string;
			} = {};

			if (!isForgotPassword) {
				if (!values.loginUsername) {
					errors.loginUsername = 'Username is required';
				}
				if (passwordErrors(values.loginPassword)) {
					errors.loginPassword = passwordErrors(values.loginPassword);
				}
			}

			if (isForgotPassword && !otp) {
				if (!values.resetEmail) {
					errors.resetEmail = 'Email is required';
				} else if (emailErrors(values.resetEmail)) {
					errors.resetEmail = emailErrors(values.resetEmail);
				}
			}

			if (otp) {
				if (!values.resetOTP && formik.touched.resetOTP) {
					errors.resetOTP = 'OTP is required';
				} else if (values.resetOTP && errorOTP(values.resetOTP)) {
					errors.resetOTP = errorOTP(values.resetOTP);
				}

				if (!values.resetNewPassword && formik.touched.resetNewPassword) {
					errors.resetNewPassword = 'New password is required';
				} else if (values.resetNewPassword && passwordErrors(values.resetNewPassword)) {
					errors.resetNewPassword = passwordErrors(values.resetNewPassword);
				}
				if (!values.resetNewConfirmPassword && formik.touched.resetNewConfirmPassword) {
					errors.resetNewConfirmPassword = 'Confirm password is required';
				} else if (
					values.resetNewConfirmPassword &&
					passwordErrors(values.resetNewConfirmPassword)
				) {
					errors.resetNewConfirmPassword = passwordErrors(values.resetNewConfirmPassword);
				} else if (
					values.resetNewPassword &&
					values.resetNewConfirmPassword &&
					errorConfirmPassword(values.resetNewPassword, values.resetNewConfirmPassword)
				) {
					errors.resetNewConfirmPassword = 'Passwords do not match';
				}
			}

			return errors;
		},
		validateOnChange: true,
		validateOnBlur: true,
		onSubmit: async (values) => {
			if (isForgotPassword && otp) {
				let hasErrors = false;
				if (!values.resetOTP) {
					formik.setFieldError('resetOTP', 'OTP is required');
					hasErrors = true;
				} else if (errorOTP(values.resetOTP)) {
					formik.setFieldError('resetOTP', errorOTP(values.resetOTP));
					hasErrors = true;
				}
				if (!values.resetNewPassword) {
					formik.setFieldError('resetNewPassword', 'New password is required');
					hasErrors = true;
				} else if (passwordErrors(values.resetNewPassword)) {
					formik.setFieldError(
						'resetNewPassword',
						passwordErrors(values.resetNewPassword),
					);
					hasErrors = true;
				}
				if (!values.resetNewConfirmPassword) {
					formik.setFieldError('resetNewConfirmPassword', 'Confirm password is required');
					hasErrors = true;
				} else if (passwordErrors(values.resetNewConfirmPassword)) {
					formik.setFieldError(
						'resetNewConfirmPassword',
						passwordErrors(values.resetNewConfirmPassword),
					);
					hasErrors = true;
				} else if (
					errorConfirmPassword(values.resetNewPassword, values.resetNewConfirmPassword)
				) {
					formik.setFieldError('resetNewConfirmPassword', 'Passwords do not match');
					hasErrors = true;
				}

				if (!hasErrors) {
					setIsLoading(true);
					try {
						await centralApi.post(`userService/user/changeForgotPassword`, {
							email: values.resetEmail,
							password: values.resetNewPassword,
							otp: values.resetOTP,
						});
						toast.success('Password reset successfully!', {
							closeOnClick: true,
						});
						handleBackToLogin();
						setIsLoading(false);
					} catch (error) {
						formik.setFieldError('resetOTP', 'Invalid OTP');
						setIsLoading(false);
					}
				} else {
					setIsLoading(false);
				}
			} else if (!singUpStatus && !isForgotPassword) {
				if (!values.loginUsername || !values.loginPassword) {
					if (!values.loginUsername) {
						formik.setFieldError('loginUsername', 'Username is required');
					}
					if (!values.loginPassword) {
						formik.setFieldError('loginPassword', 'Password is required');
					}
					setIsLoading(false);
					return;
				}
				setIsLoading(true);
				try {
					const res = await centralApi.post(`userService/user/login`, {
						username: values.loginUsername,
						password: values.loginPassword,
					});
					localStorage.setItem('HRMS-token', res.data.data.authToken);
					localStorage.setItem('HRMS-data', JSON.stringify(res.data.data));
					const fullUser = res.data.data;
					const nameUserOnly = {
						fName: fullUser.fName,
						lName: fullUser.lName,
					};
					localStorage.setItem('HRMS-user', JSON.stringify(nameUserOnly));
					setUser(nameUserOnly);
					toast.dismiss();
					toast.success('Login successful!', {
						closeOnClick: true,
					});
					handleOnClick();
					setIsLoading(false);
				} catch (error) {
					// console.error('Error during login:', error);
					if (
						error &&
						typeof error === 'object' &&
						'response' in error &&
						error.response &&
						typeof (error as any).response === 'object'
					) {
						const err = error as any;
						const status = err.response?.status;
						const message = err.response?.data?.msg;

						if (status === 400 || status === 403) {
							toast.error(message || 'Access denied.', { closeOnClick: true });
							setIsLoading(false);
							return;
						}
					}
					formik.setFieldError('loginPassword', 'Invalid username or password');
					setIsLoading(false);

				}
			}
		},
	});

	const isResetButtonDisabled =
		!formik.values.resetOTP ||
		!formik.values.resetNewPassword ||
		!formik.values.resetNewConfirmPassword ||
		!!formik.errors.resetOTP ||
		!!formik.errors.resetNewPassword ||
		!!formik.errors.resetNewConfirmPassword;

	const renderForm = () => {
		if (isForgotPassword && otp) {
			return (
				<>
					<FormGroup id='resetOTP' >
						<Label
							htmlFor='resetOTP'
							style={{
								marginLeft: '4px',
								fontWeight: 'bold',
								color: "white opacity-75",
							}}
							size='lg'>
							Enter OTP
						</Label>
						<Input
							id='resetOTP'
							type='text'
							placeholder='Enter your OTP'
							autoComplete='current-otp'
							name='resetOTP'
							value={formik.values.resetOTP || ''}
							isTouched={!!formik.touched.resetOTP}
							invalidFeedback={formik.errors.resetOTP || undefined}
							isValid={formik.isValid && !formik.errors.resetOTP}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							style={{
								borderRadius: '10px',
								padding: '1rem 0 1rem 1rem',
								border: "1px solid #2D3A56",
								transition: 'all 0.3s ease',
								background: "#0a1023",
								color: "white",
								paddingRight: '3rem',
							}}
						/>
					</FormGroup>
					<FormGroup id='resetNewPassword' >
						<Label
							htmlFor='resetNewPassword'
							style={{
								marginLeft: '4px',
								fontWeight: 'bold',
								color: "white opacity-75",
							}}
							size='lg'>
							Password
						</Label>
						<div style={{ position: 'relative' }}>
							<Input
								id='resetNewPassword'
								type={viewPass ? 'text' : 'password'}
								placeholder='Enter new password'
								autoComplete='current-password'
								value={formik.values.resetNewPassword || ''}
								isTouched={!!formik.touched.resetNewPassword}
								invalidFeedback={formik.errors.resetNewPassword || undefined}
								isValid={formik.isValid && !formik.errors.resetNewPassword}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								style={{
									borderRadius: '10px',
									padding: '1rem 0 1rem 1rem',
									border: "1px solid #2D3A56",
									transition: 'all 0.3s ease',
									background: "#0a1023",
									color: "white",
									paddingRight: '3rem',
								}}
							/>
							<button
								type='button'
								onClick={() => setViewPass((prev) => !prev)}
								style={{
									position: 'absolute',
									top:
										formik.errors.resetNewPassword &&
											formik.touched.resetNewPassword
											? '40%'
											: '50%',
									right: '1rem',
									transform: 'translateY(-50%)',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
								}}>
								{viewPass ? <EyeOff size={20} /> : <Eye size={20} />}
							</button>
						</div>
					</FormGroup>
					<FormGroup id='resetNewConfirmPassword' >
						<Label
							htmlFor='resetNewConfirmPassword'
							style={{
								marginLeft: '4px',
								fontWeight: 'bold',
								color: "white opacity-75",
							}}
							size='lg'>
							Confirm Password
						</Label>
						<div style={{ position: 'relative' }}>
							<Input
								id='resetNewConfirmPassword'
								type={viewConPass ? 'text' : 'password'}
								placeholder='Enter confirm password'
								value={formik.values.resetNewConfirmPassword || ''}
								isTouched={!!formik.touched.resetNewConfirmPassword}
								invalidFeedback={formik.errors.resetNewConfirmPassword || undefined}
								isValid={formik.isValid && !formik.errors.resetNewConfirmPassword}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								style={{
									borderRadius: '10px',
									padding: '1rem 0 1rem 1rem',
									border: "1px solid #2D3A56",
									transition: 'all 0.3s ease',
									background: "#0a1023",
									color: "white",
									paddingRight: '3rem',
								}}
							/>
							<button
								type='button'
								onClick={() => setViewConPass((prev) => !prev)}
								style={{
									position: 'absolute',
									top:
										formik.errors.resetNewConfirmPassword &&
											formik.touched.resetNewConfirmPassword
											? '35%'
											: '50%',
									right: '1rem',
									transform: 'translateY(-50%)',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
								}}>
								{viewConPass ? <EyeOff size={20} /> : <Eye size={20} />}
							</button>
						</div>
					</FormGroup>
					<div className='d-flex justify-content-end'>
						<Button
							onClick={handleForgotPassword}
							color='link'
							style={{
								color: darkModeStatus ? '#ff8e53' : '#ff6b6b',
								textDecoration: 'none',
								fontWeight: '500',
								transition: 'color 0.3s ease',
							}}
							aria-label='Back to login'>
							{forgotText}
						</Button>
					</div>
				</>
			);
		}
		if (isForgotPassword) {
			return (
				<>
					<FormGroup id='resetEmail' style={{ marginBottom: '1.5rem' }}>
						<Label
							htmlFor='resetEmail'
							style={{
								marginLeft: '4px',
								fontWeight: 'bold',
								color: "white opacity-75",
							}}
							size='lg'>
							Email
						</Label>
						<Input
							id='resetEmail'
							type='email'
							placeholder='Enter your email'
							autoComplete='email'
							name='resetEmail'
							value={formik.values.resetEmail || ''}
							isTouched={!!formik.touched.resetEmail}
							invalidFeedback={formik.errors.resetEmail || undefined}
							isValid={formik.isValid && !formik.errors.resetEmail}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							style={{
								borderRadius: '10px',
								padding: '1rem',
								border: "1px solid #2D3A56",
								transition: 'all 0.3s ease',
								background: "#0a1023",
								color: "white",
							}}
						/>
					</FormGroup>
					<div className='d-flex justify-content-end'>
						<Button
							onClick={handleForgotPassword}
							color='link'
							style={{
								color: darkModeStatus ? '#ff8e53' : '#ff6b6b',
								textDecoration: 'none',
								fontWeight: '500',
								transition: 'color 0.3s ease',
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.color = darkModeStatus
									? '#ff9f6b'
									: '#e65b5b';
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.color = darkModeStatus
									? '#ff8e53'
									: '#ff6b6b';
							}}
							aria-label='Back to login'>
							{forgotText}
						</Button>
					</div>
				</>
			);
		}
		return (
			<div className='col-12'>
				<FormGroup id='loginUsername' style={{ marginBottom: '1.5rem' }}>
					<Label
						htmlFor='loginUsername'
						style={{
							marginLeft: '4px',
							fontWeight: 'bold',
							color: "white opacity-75",
						}}
						size='lg'>
						Email
					</Label>
					<Input
						id='loginUsername'
						autoComplete='username'
						placeholder='Enter your email'
						value={formik.values.loginUsername || ''}
						isTouched={!!formik.touched.loginUsername}
						invalidFeedback={formik.errors.loginUsername || undefined}
						isValid={formik.isValid && !formik.errors.loginUsername}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						style={{
							borderRadius: '10px',
							padding: '1rem 0 1rem 1rem',
							border: "1px solid #2D3A56",
							transition: 'all 0.3s ease',
							background: "#0a1023",
							color: "white",
						}}
					/>
				</FormGroup>
				<FormGroup id='loginPassword' style={{ marginBottom: '1.5rem' }}>
					<Label
						htmlFor='loginPassword'
						style={{
							marginLeft: '4px',
							fontWeight: 'bold',
							color: "white opacity-75",
						}}
						size='lg'>
						Password
					</Label>
					<div style={{ position: 'relative' }}>
						<Input
							id='loginPassword'
							type={showPassword ? 'text' : 'password'}
							placeholder='Enter your password'
							autoComplete='current-password'
							value={formik.values.loginPassword || ''}
							isTouched={!!formik.touched.loginPassword}
							invalidFeedback={formik.errors.loginPassword || undefined}
							isValid={formik.isValid && !formik.errors.loginPassword}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							style={{
								borderRadius: '10px',
								padding: '1rem 0 1rem 1rem',
								border: "1px solid #2D3A56",
								transition: 'all 0.3s ease',
								background: "#0a1023",
								color: "white",
								paddingRight: '3rem',
							}}
						/>
						<button
							type='button'
							onClick={() => setShowPassword((prev) => !prev)}
							style={{
								position: 'absolute',
								top:
									formik.errors.loginPassword && formik.touched.loginPassword
										? '40%'
										: '50%',
								right: '1rem',
								transform: 'translateY(-50%)',
								background: 'none',
								border: 'none',
								cursor: 'pointer',
							}}>
							{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
						</button>
					</div>
				</FormGroup>
				<div className='d-flex justify-content-end'>
					<Button
						onClick={handleForgotPassword}
						color='link'
						style={{
							color: "#4ECDC4",
							textDecoration: "none",
							fontWeight: "500",
							transition: "color 0.3s ease",
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.color = "#6FE7E0";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.color = "#4ECDC4";
						}}
						aria-label='Forgot password'>
						Forgot Password?
					</Button>
				</div>
			</div>
		);
	};

	return (
		<PageWrapper
			isProtected={false}
			title={singUpStatus ? 'Sign Up' : 'Login'}
			className='dark-bg'>
			<Page className='p-0'>
				<div className="container-fluid">
					<div className="bg-dots" />
					<div className="row min-vh-100 align-items-center justify-content-center">
						{/* LEFT SIDE */}
						<div className="col-lg-5 d-none d-lg-flex justify-content-center">
							<div
								className="d-flex flex-column justify-content-center"
								style={{
									maxWidth: "500px",
									width: "100%",
								}}
							>

								<div className="text-start mb-5">
									<img
										src="/logo.png"
										width={180}
										alt=""
									/>
								</div>

								<div className="mb-5">
									<h2 className="fw-bold text-white">
										Streamline <span style={{ color: "#F35366" }}>HR.</span>
									</h2>

									<h2 className="fw-bold text-white">
										Empower <span style={{ color: "#09B5AD" }}>People.</span>
									</h2>
								</div>

								<p
									className="text-light opacity-75 fs-5 "
									style={{ maxWidth: 400 }}
								>
									Your all-in-one solution for smarter HR management.
								</p>
								<div className=" d-flex justify-content-start">
									<img
										src="/main.png"
										className="img-fluid"
										style={{ maxWidth: "350px" }}
										alt=""
									/>
								</div>
								<div className="d-flex mb-5">
									<div
										className="me-3 d-flex align-items-center justify-content-center"
										style={{
											width: "42px",
											height: "42px",
											borderRadius: "12px",
											background: "rgba(13, 202, 240, 0.15)", // Info color with opacity
										}}
									>
										<BsShieldCheck
											size={22}
											style={{ color: "#0dcaf0" }}
										/>
									</div>

									<div>
										<h6 className="text-white mb-1">
											Secure & Reliable
										</h6>

										<small className="text-light opacity-75">
											Your data is safe with us
										</small>
									</div>
								</div>

								<div className="d-flex mb-5">
									<div
										className="me-3 d-flex align-items-center justify-content-center"
										style={{
											width: "42px",
											height: "42px",
											borderRadius: "12px",
											background: "rgba(13, 110, 253, 0.15)", // 15% opacity
										}}
									>
										<BsClockHistory
											size={22}
											style={{ color: "#0d6efd" }}
										/>
									</div>

									<div>
										<h6 className="text-white mb-1">
											Save Time
										</h6>

										<small className="text-light opacity-75">
											Automate HR processes
										</small>
									</div>
								</div>

								<div className="d-flex">
									<div
										className="me-3 d-flex align-items-center justify-content-center"
										style={{
											width: "42px",
											height: "42px",
											borderRadius: "12px",
											background: "rgba(220, 53, 69, 0.15)", // Danger color with opacity
										}}
									>
										<BsPeople
											size={22}
											style={{ color: "#dc3545" }}
										/>
									</div>

									<div>
										<h6 className="text-white mb-1">
											Better Collaboration
										</h6>

										<small className="text-light opacity-75">
											Connect and grow together
										</small>
									</div>
								</div>
							</div>
						</div>
						<div className="col-lg-5 d-flex justify-content-center justify-content-lg-end pe-lg-5" >
							<Card

								className='login-card w-100'
								data-tour='login-page'
								style={{
									borderRadius: '20px',
									border: " 3px solid #2D3A56",
									boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
									background: '#0f1628',
								}}>
								<CardBody style={{ padding: '2.5rem' }}>
									<div className="d-flex justify-content-center align-items-center my-4">
										<img
											src="/logo.png"
											alt="HRMS Logo"
											className="logo-img"
										/>
									</div>

									<LoginHeader isForgot={isForgotPassword} isOtpScreen={otp} />

									<form
										className='row g-4'
										style={{ maxWidth: '400px', margin: '0 auto' }}
										onSubmit={(e) => {
											if (isLoading) {
												e.preventDefault(); // Stop the double submit
												return;
											}
											formik.handleSubmit(e); // Continue normal submit
										}}>
										{renderForm()}
										<div className='col-12'>
											{isForgotPassword ? (
												<Button
													color='warning'
													className='w-100 py-3 hrms-gradient-btn'
													onClick={async () => {
														if (otp) {
															setIsLoading(true);
															formik.submitForm();
														} else {
															// Validate the email field first
															const errors = await formik.validateForm();

															// Mark the field as touched so the error is displayed
															formik.setFieldTouched("resetEmail", true);

															if (!errors.resetEmail) {
																handleOTP();
															}
														}
													}}
													isDisable={otp && isResetButtonDisabled}
													aria-label={otp ? 'Reset Password' : 'Send OTP'}>
													{isLoading && <Spinner isSmall inButton isGrow />}
													{otp ? 'Reset Password' : 'Send OTP'}
												</Button>
											) : (
												<Button
													type="submit"
													color="warning"
													className="w-100 py-3 hrms-gradient-btn"
													isDisable={
														isLoading ||
														!!formik.errors.loginPassword ||
														!formik.values.loginPassword
													}
													aria-label="Login"
												>
													{isLoading && <Spinner isSmall inButton isGrow />}
													Login
												</Button>
											)}
										</div>
									</form>
								</CardBody>
							</Card>
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

Login.propTypes = {
	isSignUp: PropTypes.bool,
};
Login.defaultProps = {
	isSignUp: false,
};

export default Login;